import { orderBy } from 'natural-orderby';
import { defaultViewKey } from '../shared/constants/constants';
import systemPanels from './systemPanels/systemPanelConstants';
import { isRequiredFiltersHandled } from '../shared/filtering/filtersModalUtils';

export function savedFiltersForDashboardAndView(
    allSavedFilters,
    dashboardKey,
    viewKey
) {
    const dashboardViewKey = `${dashboardKey}_${viewKey}`;

    return allSavedFilters.filter((savedFilter) =>
        savedFilter.key.toLowerCase().startsWith(dashboardViewKey.toLowerCase())
    );
}

export function buildSavedFilterOption(dashboardKey, viewKey, savedFilter) {
    return {
        textField: savedFilter.name,
        valueField: {
            dashboardKey,
            viewKey,
            savedFilterKey: savedFilter.key,
        },
    };
}

export function defaultSavedFilterOption(
    dashboardLabels,
    dashboardKey,
    viewKey
) {
    return {
        textField: dashboardLabels.default,
        valueField: { dashboardKey, viewKey, savedFilterKey: undefined },
    };
}
/* eslint-disable complexity */
export function getSavedFilterOptions(
    dashboardKey,
    view,
    allSavedFilters,
    selectOptions,
    dashboardLabels
) {
    let requiredFiltersHandled = false;
    try {
        requiredFiltersHandled = isRequiredFiltersHandled(
            view.requiredFilterKeys,
            view.filterValues,
            view.filterConfigurations,
            selectOptions
        );
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`getSavedFilterOptions: ${error}`);
    }

    const defaultViewOption = defaultSavedFilterOption(
        dashboardLabels,
        dashboardKey,
        view.viewKey
    );

    const savedFilters = savedFiltersForDashboardAndView(
        allSavedFilters,
        dashboardKey,
        view.viewKey
    );

    if (!savedFilters?.length) {
        return requiredFiltersHandled ? [defaultViewOption] : [];
    }

    const filterOptions = orderBy(savedFilters, 'name').map((savedFilter) =>
        buildSavedFilterOption(dashboardKey, view.viewKey, savedFilter)
    );

    return requiredFiltersHandled
        ? [defaultViewOption, ...filterOptions]
        : filterOptions;
}
/* eslint-enable complexity */

export function systemPanelOptions(dashboardLabels) {
    return systemPanels.map((systemPanel) => {
        const { panelTitleOverride } = systemPanel.landingPageWidget;

        return {
            groupingLabel: dashboardLabels.quickLinks,
            textField:
                dashboardLabels[panelTitleOverride] ?? panelTitleOverride,
            valueField: systemPanel.landingPageWidget,
        };
    });
}

// eslint-disable-next-line complexity
function buildLandingPageWidgetOption(dashboard, view, widget) {
    if (!widget.landingPageConfig) {
        return null;
    }

    if (!widget.landingPageConfig.title && !widget.title) {
        // eslint-disable-next-line no-console
        console.warn(
            `Title not found for Landing Page Widget: ${widget.landingPageConfig.id}`
        );
    }

    return {
        textField: widget.landingPageConfig.title ?? widget.title,
        valueField: {
            dashboardKey: dashboard.id,
            dashboardLabel: dashboard.configuration.views[0].title,
            viewKey: view.viewKey,
            landingPageWidgetId: widget.landingPageConfig.id,
        },
    };
}

export function getLandingPageWidgetOptions(dashboardConfigs) {
    const options = [];

    dashboardConfigs.forEach((dashboard) =>
        dashboard.configuration?.views?.forEach((view) => {
            const viewWidgets = view.widgets.reduce((result, widget) => {
                const landingPageWidgetOption = buildLandingPageWidgetOption(
                    dashboard,
                    view,
                    widget
                );

                return landingPageWidgetOption
                    ? [...result, landingPageWidgetOption]
                    : result;
            }, []);

            if (view.landingPageWidget) {
                viewWidgets.push({
                    textField: view.landingPageWidget.title,
                    valueField: {
                        dashboardKey: dashboard.id,
                        dashboardLabel: dashboard.configuration.views[0].title,
                        viewKey: view.viewKey,
                        landingPageWidgetId: undefined, // undefined indicates using explicit landing page widget
                    },
                });
            }

            options.push({
                label: `${view.title} ${
                    view.viewKey !== defaultViewKey ? view.viewKey : ''
                }`,
                options: orderBy(viewWidgets, 'textField'),
            });
        })
    );

    return orderBy(options, 'label');
}
